import React, { useMemo, useState, useContext } from "react";
import { useTranslation } from 'react-i18next';
import { Button, Icon } from 'semantic-ui-react';
import { useSelector } from 'react-redux';
import { UserContext } from 'src/context';
import { Mention } from 'react-mentions';
import { useCompanyMentionUsers } from 'src/hooks/useCompanyMentionUsers';
import CommentMentionsInput from 'src/components/general/CommentMentionsInput';
import CommentMentionText from 'src/components/general/CommentMentionText';
import { renderMentionSuggestion } from 'src/components/general/MentionSuggestionItem';
import moment from 'moment';
import { getMomentLocale } from 'src/utils/appLocale';
import './CommentsTab.css';
import 'src/components/general/mentionSuggestions.scss';

/**
 * Convierte el markup de react-mentions @[display](id) → @display
 * Soporta ids alfanuméricos (MongoDB ObjectId) y numéricos.
 */
export const cleanMentionMarkup = (text) =>
  text.replace(/@\[(.*?)\]\((.*?)\)/g, '@$1');

/**
 * Componente de comentarios unificado.
 *
 * Props:
 *  - notes:            array de { user: { id, name, email }, date, comment }
 *  - companyId:        string
 *  - onSubmitComment:  ({ cleanedValue, mentions }) => Promise<void>
 *  - onDeleteComment:  (index) => void  — opcional
 *  - isSubmitting:     boolean
 */
const CommentsTab = ({
  notes = [],
  companyId,
  onSubmitComment,
  onDeleteComment,
  isSubmitting = false,
}) => {
  const { t } = useTranslation('finance');
  const userInfo = useContext(UserContext);
  const currentUser = useSelector((state) => state.auth);

  const [newNote, setNewNote] = useState('');
  const [mentionedUsers, setMentionedUsers] = useState([]);
  const userId = currentUser?.uid;

  const mentionUsers = useCompanyMentionUsers(companyId, userId);

  const handleNoteChange = (_event, newValue, _plain, mentions) => {
    setNewNote(newValue);
    setMentionedUsers(mentions || []);
  };

  const handleSubmit = async () => {
    if (!newNote.trim()) return;
    const cleanedValue = cleanMentionMarkup(newNote);
    const mentions = mentionedUsers.map((m) => m.display.replace(/^@/, ''));
    await onSubmitComment({ cleanedValue, mentions });
    setNewNote('');
    setMentionedUsers([]);
  };

  const initials = (name, email) => {
    if (name) {
      const parts = name.trim().split(/\s+/).filter(Boolean);
      if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      return parts[0]?.substring(0, 2).toUpperCase() || 'U';
    }
    return (email?.split('@')[0] || '').substring(0, 2).toUpperCase() || 'U';
  };

  return (
    <div className="comments-tab-container">
      <div className="comments-wrapper">
        {/* Input nuevo comentario */}
        <div className="new-comment">
          <div className="avatar">
            {initials(userInfo?.name, userInfo?.email)}
          </div>
          <div className="comment-box">
            <CommentMentionsInput
              value={newNote}
              onChange={handleNoteChange}
              placeholder={t('comments.placeholder')}
            >
              <Mention
                trigger="@"
                data={mentionUsers}
                displayTransform={(_id, display) => `@${display}`}
                renderSuggestion={renderMentionSuggestion}
                appendSpaceOnAdd
              />
            </CommentMentionsInput>
            <div className="controls">
              <Button
                primary
                onClick={handleSubmit}
                className="publish-btn mt-2"
                loading={isSubmitting}
                disabled={!newNote.trim() || isSubmitting}
              >
                <span>{t('comments.publish')}</span>
              </Button>
            </div>
          </div>
        </div>

        {/* Lista de comentarios */}
        <div className="comments-list">
          {notes.length > 0 ? (
            notes
              .slice(0)
              .reverse()
              .map((elem, index) => (
                <div key={index} className="comment-item">
                  <div className="avatar">
                    {initials(elem.user?.name, elem.user?.email)}
                  </div>
                  <div className="comment-body">
                    <div className="comment-header">
                      <div className="user">{elem.user?.email || elem.user?.name}</div>
                      <div className="time">{moment(elem.date).locale(getMomentLocale()).fromNow()}</div>
                      {onDeleteComment && elem.user?.id === userId && (
                        <Icon
                          name="trash"
                          className="delete-icon"
                          onClick={() => onDeleteComment(index)}
                        />
                      )}
                    </div>
                    <div className="comment-text">
                      <CommentMentionText text={elem.comment} mentionClassName="mention" />
                    </div>
                  </div>
                </div>
              ))
          ) : (
            <div className="no-comments">
              <p>{t('comments.noComments')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CommentsTab;
